/** @jsxImportSource theme-ui */
import React, { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, ButtonGroup, ButtonGroupOnValueChangeParam } from '@infotrack/zenith-ui';
import { useITopFiveApiV2EndpointsAuthMeEndpoint } from '@/api/api.generated';
import { QUERY_CONFIG } from '@/api/queryConstants';
import { navigationMenuStyles } from './NavigationMenu.styles';

/**
 * Main navigation menu for the application header
 * Displays in the InfoTrack header bar with logo
 */
export const NavigationMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch user permissions
  const { data: authResponse } = useITopFiveApiV2EndpointsAuthMeEndpoint({
    query: {
      enabled: true,
      staleTime: QUERY_CONFIG.STALE_TIME.MEDIUM,
    },
  });

  const apiUser = authResponse?.data;

  // Check if user has permission to access Staff page
  const hasStaffAccess = useMemo(() => {
    return (
      apiUser?.permissions?.hasiNominateAccess ||
      apiUser?.permissions?.hasiTopFiveStaffEditorAccess ||
      false
    );
  }, [apiUser?.permissions?.hasiNominateAccess, apiUser?.permissions?.hasiTopFiveStaffEditorAccess]);

  // Check if user has permission to access Calculate Bonuses page
  const hasCalculateBonusesAccess = useMemo(() => {
    return apiUser?.permissions?.hasCalculateTopFiveBonusesAccess || false;
  }, [apiUser?.permissions?.hasCalculateTopFiveBonusesAccess]);

  const navigationOptions = useMemo(() => {
    const options = [
      { value: '/topfive', text: 'iTop5' },
    ];

    // Only show Staff menu item if user has permission
    if (hasStaffAccess) {
      options.push({ value: '/staff', text: 'Staff' });
    }

    // Only show Calculate Bonuses menu item if user has permission
    if (hasCalculateBonusesAccess) {
      options.push({ value: '/bonuses', text: 'Calculate Bonuses' });
    }

    return options;
  }, [hasStaffAccess, hasCalculateBonusesAccess]);

  const selectedValue = useMemo(() => {
    const pathname = location.pathname;
    // Match the base path for each route
    if (pathname.startsWith('/staff')) return '/staff';
    if (pathname.startsWith('/bonuses')) return '/bonuses';
    // Default to /topfive for root and detail pages
    return '/topfive';
  }, [location.pathname]);

  const handleNavigationChange = (param: ButtonGroupOnValueChangeParam) => {
    navigate(param.selectedOption.value as string);
  };

  return (
    <Box sx={navigationMenuStyles}>
      <ButtonGroup
        options={navigationOptions}
        value={selectedValue}
        onValueChange={handleNavigationChange}
        size="small"
      />
    </Box>
  );
};
